import {
  FontFallbackMetrics,
  FontFallbackOverrides,
  FontFallbackWeights,
  FontWeightRange
} from '../../types/font'

const FALLBACK_LOCAL_FONTS: Record<
  keyof FontFallbackMetrics,
  Record<keyof FontFallbackWeights, string[]>
> = {
  arial: {
    regular: ['Arial', 'ArialMT', 'Helvetica', 'Liberation Sans', 'LiberationSans'],
    bold: [
      'Arial Bold',
      'Arial-BoldMT',
      'Helvetica Bold',
      'Liberation Sans Bold',
      'LiberationSans-Bold'
    ]
  },
  roboto: {
    regular: ['Roboto', 'Roboto-Regular'],
    bold: ['Roboto Bold', 'Roboto-Bold']
  }
}

const FALLBACK_WEIGHT_RANGES: Record<keyof FontFallbackWeights, FontWeightRange> = {
  regular: [1, 549],
  bold: [550, 1000]
}

const FALLBACK_SYSTEM_FONTS = Object.keys(FALLBACK_LOCAL_FONTS) as (keyof FontFallbackMetrics)[]
const FALLBACK_WEIGHTS = Object.keys(FALLBACK_WEIGHT_RANGES) as (keyof FontFallbackWeights)[]

export const getFontFallbackFamily = (fontName: string, systemFont: keyof FontFallbackMetrics) =>
  `${fontName}-fallback-${systemFont}`

const getFallbackFaceRule = (
  family: string,
  localFonts: string[],
  [minWeight, maxWeight]: FontWeightRange,
  overrides: FontFallbackOverrides
) => `
@font-face {
  font-family: '${family}';
  src: ${localFonts.map((localFont) => `local('${localFont}')`).join(', ')};
  font-weight: ${minWeight} ${maxWeight};
  size-adjust: ${overrides.sizeAdjust}%;
  ascent-override: ${overrides.ascentOverride}%;
  descent-override: ${overrides.descentOverride}%;
  line-gap-override: ${overrides.lineGapOverride}%;
}`

export const getFontFallbackFamilies = (fontName: string, metrics?: FontFallbackMetrics) =>
  metrics
    ? FALLBACK_SYSTEM_FONTS.map((systemFont) => getFontFallbackFamily(fontName, systemFont))
    : []

export const getFontFallbackFaceRules = (fontName: string, metrics?: FontFallbackMetrics) => {
  if (!metrics) return ''

  return FALLBACK_SYSTEM_FONTS.flatMap((systemFont) =>
    FALLBACK_WEIGHTS.map((weight) =>
      getFallbackFaceRule(
        getFontFallbackFamily(fontName, systemFont),
        FALLBACK_LOCAL_FONTS[systemFont][weight],
        FALLBACK_WEIGHT_RANGES[weight],
        metrics[systemFont][weight]
      )
    )
  ).join('\n')
}
